import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, ClickAwayListener, TextField, Autocomplete } from '@mui/material';
import { Search as SearchIcon, ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material/';
import { useSelector, useDispatch } from 'react-redux';
import classes from '../filter/Filter.module.scss';
import { FilterProps } from '../../interfaces/props/FilterProps';
import { setSites, setTypes } from '../../../store/filters/filters.slice';
import FiltersService from '../../services/filters.service';
import { FilterOption } from '../../interfaces/FilterOption';
import { FilterMapper } from '../../interfaces/FilterMapper';
import { FilterNames } from '../../enums/FilterNames';
import DetectMobileHelper from '../../helpers/detect-mobile.helper';
import { RootState } from '../../../store';

const filterMapper: FilterMapper = {
  [FilterNames.TYPE]: setTypes,
  [FilterNames.SITE]: setSites,
};

const Filter = ({ name }: FilterProps) => {
  const filters = useSelector(
    (state: RootState) => state.filters.filters
  );
  const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false);
  const [options, setOptions] = useState<FilterOption[]>([]);
  const dispatch = useDispatch();

  const filterValuesMapper = useMemo(
    () => ({
      [FilterNames.TYPE]: filters.types ?? [],
      [FilterNames.SITE]: filters.sites ?? []
    }),
    [filters]
  );

  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(
    filters[name] || filterValuesMapper[name] || []
  );

  useEffect(() => {
    setSelectedFilters(filterValuesMapper[name]);
  }, [filters, name, filterValuesMapper]);

  useEffect(() => {
    if (!isDropdownOpened) {
      FiltersService.getOptionsByFilterName(name)
        .then((data) => {
          setOptions(data);
        })
        .catch((err) => console.error(err));
    }
  }, [isDropdownOpened, name]);

  const dispatchFilters = useCallback(() => {
    const dispatchAction = filterMapper[name];

    if (!dispatchAction) {
      return;
    }
    dispatch(
      dispatchAction(selectedFilters)
    );
  }, [dispatch, selectedFilters, name]);

  useEffect(() => {
    dispatchFilters();
  }, [selectedFilters, dispatchFilters]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatchFilters();
    setIsDropdownOpened(!isDropdownOpened);
  };

  const handleClose = () => {
    dispatchFilters();
    setIsDropdownOpened(false);
  };

  const renderAutocomplete = useMemo(() => {
    return (
      <Autocomplete
        className={`${classes['search']} mt-2`}
        classes={{ option: classes['option-color'], listbox: `${classes['listbox-height']} scrollbar`, noOptions: classes['no-options']}}
        options={options}
        getOptionLabel={(option) => option.name}
        multiple
        renderInput={(params) => (
          <TextField {...params} autoFocus placeholder='Search...'
            InputProps={{
              ...params.InputProps,
              classes: {
                notchedOutline: classes['autocomplete-search'],
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={`No ${name}`}
        limitTags={1}
        open={true}
        onChange={(event, value) => {
          setSelectedFilters(value);
          dispatchFilters();
        }}
        value={selectedFilters}
        popupIcon={<SearchIcon className={`${classes['rotate-icon']} pe-1`} />}
      />
    );
  }, [options, selectedFilters, dispatchFilters, name]);

  const getFiltersLabel = useMemo(() => {
    if (0 < selectedFilters.length) {
      return 1 === selectedFilters.length
        ? selectedFilters[0].name
        : `${selectedFilters[0].name} + ${selectedFilters.length - 1}`;
    }

    return name;
  }, [selectedFilters, name]);

  return (
    <Box>
      <Button onClick={handleClick}
        className={`button-text filter-button ${isDropdownOpened ? classes.selectedFilterButton : classes.filterButton}`}
        endIcon={isDropdownOpened ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}>
        {getFiltersLabel}
      </Button>
      <Box>
        {isDropdownOpened && (
          DetectMobileHelper.isMobile() ? (
            <>
              {renderAutocomplete}
            </>
          ) : (
            <ClickAwayListener onClickAway={handleClose} >
              {renderAutocomplete}
            </ClickAwayListener>
          )
        )}
      </Box>
    </Box >
  );
};

export default Filter;
